/**
 * FoamField — camera-centred persistent FOAM on the GPU (R10 of the
 * 2026-09-25 water-realism wave).
 *
 * The old whitecap memory was a CPU grid (FoamAccumulator: 128² over 3 km,
 * 24 m per texel) — trails were blotches. Crest (Bowles 2017) and Sea of
 * Thieves (Ang et al. 2018) keep a small camera-centred FEEDBACK buffer
 * instead: every frame
 *
 *     foam = max(foam_prev(p − drift·dt) · e^(−dt/τ) (+ a slight blur), source(p))
 *
 * with the SOURCE the surface's own whitecap criterion (the Gerstner
 * Jacobian folding, evaluated with the SAME TSL waves the material draws),
 * plus wakes (the interaction field's steep slopes). 512² over 256 m = half-
 * metre texels. The window follows the viewer in WHOLE texels (RippleField's
 * rule), the previous frame re-sampled at the integer shift, so trails stay
 * where they were made. Two render targets ping-pong; the material samples
 * `texture` (swap its node value each step) in world XZ around `centre`.
 *
 * A fullscreen clip-space triangle into its own target (the CausticsProjector
 * pattern) — the scene pass and its MRT are never touched. Both backends.
 */

import * as THREE from 'three/webgpu';
import { Fn, exp, float, max, positionLocal, smoothstep, texture, uniform, uv, vec2, vec4, length, min } from 'three/tsl';
import type { GerstnerWave } from './GerstnerWaves.js';
import { makeTslGerstner, type TslWaveModulation } from './tsl-gerstner.js';
import type { WaveProvider } from './WaveProvider.js';

export interface FoamFieldConfig {
  /** Texels per side. Default 512. */
  resolution?: number;
  /** World size of the window (m). Default 256. */
  size?: number;
  /** The waves the surface draws (the whitecap source). */
  waves: GerstnerWave[];
  /** The material's sea-state nodes (shared, so a gale's whitecaps are the ones drawn). */
  waveModulation?: TslWaveModulation | null;
  /** Foam lifetime (s) — e-folding of the decay. Default 6. */
  decayS?: number;
  /**
   * The WAKES' lifetime (s), when it differs from the whitecaps' (surfaces agent, 2026-09-27; research §3.3): a
   * breaking crest's foam drains in 1–3 s (Crest / HDRP keep a foam age decaying over 1–3 s) while a hull's wake lingers
   * for many seconds, so the two memories live in two channels — R the whitecaps (decayS), G the wakes (wakeDecayS) —
   * and the material takes max(R, G). Default = decayS (one memory, the old behaviour).
   */
  wakeDecayS?: number;
  /** Interaction field (wakes / splashes whiten the water): r height, gb slope, world XZ over `size`. */
  interaction?: { texture: THREE.Texture; size: number } | null;
  /**
   * Shader gaps R5: the sea's WaveProvider — a non-Gerstner one (the FFT patch) supplies the whitecap Jacobian instead
   * of `waves`, so the memory is fed by the crests the sea actually draws. Omitted / Gerstner = `waves`, exactly as before.
   */
  waveProvider?: WaveProvider | null;
}

/** CPU oracle of one texel's update (the harness pins the rule). */
export function foamStepCpu(prev: number, source: number, dt: number, decayS: number): number {
  return Math.max(prev * Math.exp(-Math.max(0, dt) / Math.max(1e-3, decayS)), source);
}

/** Whole-texel re-origin: the snapped centre and the integer texel shift from the previous centre. */
export function foamReorigin(prevX: number, prevZ: number, wantX: number, wantZ: number, texel: number): { x: number; z: number; dx: number; dz: number } {
  const x = Math.round(wantX / texel) * texel, z = Math.round(wantZ / texel) * texel;
  return { x, z, dx: Math.round((x - prevX) / texel), dz: Math.round((z - prevZ) / texel) };
}

export class FoamField {
  readonly size: number;
  readonly resolution: number;
  /** The field's centre in RENDER space (whole texels) — the material's `uFoamCenter`. */
  readonly uCenter = uniform(new THREE.Vector2(0, 0));
  readonly uTime = uniform(0);
  readonly uDt = uniform(1 / 60);
  /** Surface drift (m/s, render-space XZ): the foam travels with it. */
  readonly uDrift = uniform(new THREE.Vector2(0.4, 0.3));
  /** Whitecap threshold (the material's uFoamJacobian). */
  readonly uFoamJ = uniform(0.6);
  /** The interaction field's centre (render space), when wakes feed the foam. */
  readonly uInteractionCenter = uniform(new THREE.Vector2(0, 0));
  /** Texel shift since the previous step (the re-origin), in UV units. */
  private readonly uShift = uniform(new THREE.Vector2(0, 0));
  private readonly rts: [THREE.RenderTarget, THREE.RenderTarget];
  private readonly prevNode: { value: THREE.Texture };
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly mesh: THREE.Mesh;
  private cur = 0;
  private centerAbsX = 0;
  private centerAbsZ = 0;
  private primed = false;
  readonly stats = { steps: 0, reorigins: 0 };

  constructor(config: FoamFieldConfig) {
    const res = 2 ** Math.round(Math.log2(Math.min(1024, Math.max(64, config.resolution ?? 512))));
    this.resolution = res;
    this.size = config.size ?? 256;
    const mk = (): THREE.RenderTarget => {
      const rt = new THREE.RenderTarget(res, res, { type: THREE.HalfFloatType, depthBuffer: false });
      rt.texture.magFilter = THREE.LinearFilter;
      rt.texture.minFilter = THREE.LinearFilter;
      rt.texture.generateMipmaps = false;
      rt.texture.wrapS = rt.texture.wrapT = THREE.ClampToEdgeWrapping;
      rt.texture.colorSpace = THREE.NoColorSpace;
      return rt;
    };
    this.rts = [mk(), mk()];
    const decay = config.decayS ?? 6;
    const wakeDecay = config.wakeDecayS ?? decay;
    const size = this.size;
    const texelUv = 1 / res;
    const pField = config.waveProvider && config.waveProvider.kind !== 'gerstner' ? config.waveProvider.tsl(this.uTime, uniform(1 / 60), config.waveModulation ?? null, 0) : null;
    const gerstner = pField ? pField.surface : makeTslGerstner(config.waves, this.uTime as never, config.waveModulation ?? undefined);
    const prev = texture(this.rts[1].texture);
    this.prevNode = prev as unknown as { value: THREE.Texture };
    const inter = config.interaction ?? null;
    const interTex = inter ? texture(inter.texture) : null;
    const uCenter = this.uCenter, uDt = this.uDt, uDrift = this.uDrift, uShift = this.uShift, uFoamJ = this.uFoamJ, uIC = this.uInteractionCenter;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    // WHY top-origin (v = (1 − y_ndc)/2): three r185 samples a render target with v = 0 at its TOP row on both backends
    // (THE TOP-LEFT LAW). A bottom-origin uv wrote the field z-mirrored to what the sea samples (foam appeared at the wake's
    // mirror across the window centre) and the feedback tap read the mirrored row, flipping the memory every step.
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 1, 2, 1, 0, -1]), 2));
    geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]), 3));
    const material = new THREE.MeshBasicNodeMaterial();
    material.depthTest = false;
    material.depthWrite = false;
    material.fog = false;
    material.vertexNode = Fn(() => vec4(positionLocal.x, positionLocal.y, 0, 1))();
    material.colorNode = Fn(() => {
      const t = uv();
      // The world point this texel shows (render space). Row v runs with +z.
      const p = uCenter.add(t.sub(0.5).mul(size));
      // Where that point was in the previous window, one frame of drift upstream.
      const back = t.add(uShift).sub(uDrift.mul(uDt).div(size));
      const inside = smoothstep(0, texelUv * 2, min(min(back.x, float(1).sub(back.x)), min(back.y, float(1).sub(back.y))));
      const c = prev.sample(back).rg;
      // A slight blur (Sea of Thieves): the foam spreads and softens as it ages.
      const o = texelUv;
      const nb = prev.sample(back.add(vec2(o, 0))).rg.add(prev.sample(back.sub(vec2(o, 0))).rg)
        .add(prev.sample(back.add(vec2(0, o))).rg).add(prev.sample(back.sub(vec2(0, o))).rg).mul(0.25);
      const blurred = c.mul(0.9).add(nb.mul(0.1));
      // R = the whitecaps (decayS), G = the wakes (wakeDecayS) — see FoamFieldConfig.wakeDecayS.
      const agedR = blurred.x.mul(exp(uDt.negate().div(decay))).mul(inside);
      const agedG = blurred.y.mul(exp(uDt.negate().div(wakeDecay))).mul(inside);
      // Sources: the whitecap criterion of the drawn waves…
      const J = gerstner.jacobian(p.x, p.y);
      const source = max(float(0), float(1).sub(smoothstep(uFoamJ.sub(0.35), uFoamJ, J)));
      // …and wakes: the interaction field's steep slopes.
      let wake = float(0) as unknown as ReturnType<typeof smoothstep>;
      if (interTex && inter) {
        const iuv = p.sub(uIC).div(inter.size).add(0.5);
        const edge = smoothstep(0, 0.1, min(min(iuv.x, float(1).sub(iuv.x)), min(iuv.y, float(1).sub(iuv.y))));
        const slope = length(interTex.sample(iuv).gb).mul(edge);
        wake = smoothstep(0.05, 0.3, slope);
      }
      return vec4(max(agedR, source), max(agedG, wake), 0, 1);
    })();
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  /** The texture holding the newest foam (swap into the material every step). */
  get texture(): THREE.Texture { return this.rts[this.cur].texture; }

  /** The target holding the newest foam — for a readback (probes judge the coverage, not the step counter). */
  get renderTarget(): THREE.RenderTarget { return this.rts[this.cur]; }

  /**
   * One step: re-origin at the viewer (ABSOLUTE centre, whole texels) with the
   * rebase shift folded in (the material samples render space), then render.
   */
  step(renderer: { getRenderTarget(): unknown; setRenderTarget(rt: unknown): void; render(s: unknown, c: unknown): void }, dt: number, time: number, absX: number, absZ: number, sceneShift: { x: number; z: number }): void {
    const texel = this.size / this.resolution;
    const r = foamReorigin(this.centerAbsX, this.centerAbsZ, absX, absZ, texel);
    if (!this.primed) { r.dx = 0; r.dz = 0; this.primed = true; }
    if (r.dx !== 0 || r.dz !== 0) this.stats.reorigins++;
    this.centerAbsX = r.x; this.centerAbsZ = r.z;
    (this.uShift as unknown as { value: THREE.Vector2 }).value.set(r.dx / this.resolution, r.dz / this.resolution);
    (this.uCenter as unknown as { value: THREE.Vector2 }).value.set(r.x + sceneShift.x, r.z + sceneShift.z);
    (this.uDt as unknown as { value: number }).value = Math.min(0.1, Math.max(1 / 240, dt));
    (this.uTime as unknown as { value: number }).value = time;
    const src = this.rts[this.cur], dst = this.rts[1 - this.cur];
    this.prevNode.value = src.texture;
    const prevRt = renderer.getRenderTarget();
    renderer.setRenderTarget(dst);
    renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(prevRt);
    this.cur = 1 - this.cur;
    this.stats.steps++;
  }

  dispose(): void {
    for (const rt of this.rts) rt.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.geometry.dispose();
  }
}
